import React, { useState } from 'react';
import { api } from '../../context/AppContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { X, Target, Trash2, Plus, Loader2, Crosshair } from 'lucide-react';
import { toast } from 'sonner';

export function PRChallengeCard({ challenges, onRefresh, exerciseNames, language }) {
  const fr = language === 'fr';
  const [showCreate, setShowCreate] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState('');
  const [targetWeight, setTargetWeight] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const handleCreate = async () => {
    if (!selectedExercise || !targetWeight || parseFloat(targetWeight) <= 0) {
      toast.error(fr ? 'Choisis un exercice et un poids cible valide' : 'Choose an exercise and valid target weight');
      return;
    }
    setCreating(true);
    try {
      await api.post('/workouts/pr-challenge', {
        exercise_name: selectedExercise,
        target_weight: parseFloat(targetWeight),
      });
      toast.success(fr ? 'Défi PR créé !' : 'PR Challenge created!');
      setShowCreate(false);
      setSelectedExercise('');
      setTargetWeight('');
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || (fr ? 'Erreur' : 'Error'));
    } finally { setCreating(false); }
  };

  const handleDelete = async (challengeId) => {
    setDeleting(challengeId);
    try {
      await api.delete(`/workouts/pr-challenge/${challengeId}`);
      toast.success(fr ? 'Défi annule' : 'Challenge cancelled');
      onRefresh();
    } catch {
      toast.error(fr ? 'Erreur' : 'Error');
    } finally { setDeleting(null); }
  };

  // Filter out exercises that already have an active challenge
  const activeNames = new Set(challenges.map(c => c.exercise_name));
  const availableExercises = exerciseNames.filter(n => !activeNames.has(n));

  return (
    <Card className="border-cyan-500/20 bg-cyan-500/5" data-testid="pr-challenge-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-semibold text-foreground">
              {fr ? 'Defis PR' : 'PR Challenges'}
            </span>
            <span className="text-[10px] text-muted-foreground">
              ({challenges.length}/3)
            </span>
          </div>
          {challenges.length < 3 && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-7 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
              onClick={() => setShowCreate(!showCreate)}
              data-testid="create-pr-challenge-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              {fr ? 'Nouveau défi' : 'New challenge'}
            </Button>
          )}
        </div>

        {/* Active challenges */}
        {challenges.length > 0 && (
          <div className="space-y-2 mb-3">
            {challenges.map(c => (
              <div key={c.challenge_id} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/40" data-testid={`pr-challenge-${c.challenge_id}`}>
                <Crosshair className="w-4 h-4 text-cyan-400 shrink-0 animate-pulse" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{c.exercise_name}</p>
                  <p className="text-[10px] text-cyan-400 font-bold">
                    {fr ? 'Objectif' : 'Target'}: {c.target_weight} lbs
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(c.challenge_id)}
                  disabled={deleting === c.challenge_id}
                  className="w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors shrink-0"
                  data-testid={`delete-pr-${c.challenge_id}`}
                >
                  {deleting === c.challenge_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                </button>
              </div>
            ))}
          </div>
        )}

        {challenges.length === 0 && !showCreate && (
          <p className="text-[11px] text-muted-foreground/60 text-center py-2">
            {fr ? 'Fixe-toi un objectif de poids a battre. Jusqu\'a 3 défis simultanes.' : 'Set a weight target to beat. Up to 3 simultaneous challenges.'}
          </p>
        )}

        {/* Create form */}
        {showCreate && (
          <div className="p-3 rounded-lg bg-secondary/30 border border-cyan-500/20 space-y-3" data-testid="pr-challenge-form">
            <select
              value={selectedExercise}
              onChange={e => setSelectedExercise(e.target.value)}
              className="w-full h-8 bg-background border border-border rounded px-2 text-xs"
              data-testid="pr-challenge-exercise-select"
            >
              <option value="">{fr ? '-- Choisis un exercice --' : '-- Choose exercise --'}</option>
              {availableExercises.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  type="number"
                  value={targetWeight}
                  onChange={e => setTargetWeight(e.target.value)}
                  placeholder={fr ? 'Poids cible (lbs)' : 'Target weight (lbs)'}
                  className="w-full h-8 bg-background border border-border rounded px-2 text-xs"
                  min="1"
                  data-testid="pr-challenge-weight-input"
                />
              </div>
              <Button
                size="sm"
                className="h-8 bg-cyan-600 hover:bg-cyan-500 text-white text-xs"
                onClick={handleCreate}
                disabled={creating}
                data-testid="pr-challenge-submit-btn"
              >
                {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Target className="w-3 h-3 mr-1" />}
                {fr ? 'Créer' : 'Create'}
              </Button>
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setShowCreate(false)}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export function PRVictoryModal({ data, language, onClose }) {
  const fr = language === 'fr';
  if (!data) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="pr-victory-modal">
      <div className="relative w-full max-w-sm mx-4 animate-in zoom-in-95 duration-500">
        {/* Particle burst background */}
        <div className="absolute inset-0 overflow-hidden rounded-2xl">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full"
              style={{
                background: ['#22d3ee', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'][i % 5],
                left: `${10 + Math.random() * 80}%`,
                top: `${10 + Math.random() * 80}%`,
                animation: `ping ${1 + Math.random()}s cubic-bezier(0, 0, 0.2, 1) ${Math.random() * 0.5}s infinite`,
                opacity: 0.7,
              }}
            />
          ))}
        </div>

        <div className="relative bg-gradient-to-b from-cyan-950/90 to-background border-2 border-cyan-400/50 rounded-2xl p-6 text-center shadow-[0_0_60px_rgba(34,211,238,0.3)]">
          {/* Trophy icon with glow */}
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-cyan-500/20 flex items-center justify-center ring-4 ring-cyan-400/30 animate-bounce">
            <Target className="w-10 h-10 text-cyan-400" />
          </div>

          <h2 className="font-heading text-2xl font-black uppercase tracking-tight text-cyan-400 mb-1" data-testid="pr-victory-title">
            {fr ? 'DÉFI DETRUIT !' : 'CHALLENGE DESTROYED!'}
          </h2>

          <p className="text-sm text-foreground font-medium mb-3">{data.exercise_name}</p>

          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="text-center">
              <p className="text-[10px] uppercase text-muted-foreground">{fr ? 'Objectif' : 'Target'}</p>
              <p className="text-lg font-bold text-muted-foreground line-through">{data.target_weight} lbs</p>
            </div>
            <div className="text-2xl text-cyan-400 font-black">&rarr;</div>
            <div className="text-center">
              <p className="text-[10px] uppercase text-cyan-400">{fr ? 'Atteint' : 'Achieved'}</p>
              <p className="text-2xl font-black text-cyan-400">{data.achieved_weight} lbs</p>
            </div>
          </div>

          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold mb-4">
            +{data.points_earned || 50} XP
          </div>

          <p className="text-xs text-muted-foreground mb-4">
            {fr
              ? 'Ton coach a ete notifie. Continue a repousser tes limites !'
              : 'Your coach has been notified. Keep pushing your limits!'}
          </p>

          <Button
            onClick={onClose}
            className="w-full h-11 font-heading uppercase tracking-wider bg-cyan-600 hover:bg-cyan-500 text-white"
            data-testid="pr-victory-close-btn"
          >
            {fr ? 'CONTINUER' : 'CONTINUE'}
          </Button>
        </div>
      </div>
    </div>
  );
}
